import API_ROUTES from 'services/apiRoutes';
import { getGuestCartOwnerToken } from 'services/storage';
import { siteApi } from './siteApi';

export const GUEST_CART_OWNER_TOKEN_HEADER = 'X-Guest-Cart-Owner-Token';

// Backstop only: useSavedCart enforces one deadline across the whole restore.
const RESTORE_REQUEST_TIMEOUT_MS = 3000;

export type SavedCartStatus = 'OPEN' | 'CONVERTED' | 'EXPIRED';

export interface SavedCart {
  id: string;
  status: SavedCartStatus;
  recipient_ids: string[];
  client_id: number;
  page_query_string: string;
}

// The JWT comes from getAccessTokenSilently() at call time, so each call passes it.
type Auth = { jwt?: string };

// Only cart requests carry the guest token: it's a credential (see getGuestCartOwnerToken).
const cartHeaders = (jwt?: string) => ({
  [GUEST_CART_OWNER_TOKEN_HEADER]: getGuestCartOwnerToken(),
  ...(jwt && { Authorization: `Bearer ${jwt}` }),
});

export const savedCartApi = siteApi.injectEndpoints({
  endpoints: builder => ({
    createCart: builder.mutation<
      SavedCart,
      Auth & {
        client_id: number;
        recipient_ids: string[];
        ref_code?: string | null;
        keepalive?: boolean;
      }
    >({
      query: ({ jwt, keepalive, ...body }) => ({
        url: API_ROUTES.SAVED_DONOR_SHOPPING_CARTS,
        method: 'POST',
        body,
        headers: cartHeaders(jwt),
        keepalive,
      }),
    }),
    updateCart: builder.mutation<
      SavedCart,
      Auth & { id: string; recipient_ids: string[]; keepalive?: boolean }
    >({
      query: ({ jwt, id, recipient_ids, keepalive }) => ({
        url: API_ROUTES.SAVED_DONOR_SHOPPING_CART(id),
        method: 'PATCH',
        body: { recipient_ids },
        headers: cartHeaders(jwt),
        keepalive,
      }),
    }),
    // A mutation, not a query: it can attach this browser's guest cart to the account, and its
    // result must never come from the cache.
    getCurrentCart: builder.mutation<SavedCart | null, Auth & { client_id: number }>({
      query: ({ jwt, client_id }) => ({
        url: API_ROUTES.SAVED_DONOR_SHOPPING_CART_CURRENT,
        method: 'POST',
        body: { client_id },
        headers: cartHeaders(jwt),
        timeout: RESTORE_REQUEST_TIMEOUT_MS,
      }),
      transformResponse: (response: { cart: SavedCart | null }) => response.cart,
    }),
    completeCart: builder.mutation<SavedCart, Auth & { id: string }>({
      query: ({ jwt, id }) => ({
        url: API_ROUTES.SAVED_DONOR_SHOPPING_CART_COMPLETE(id),
        method: 'POST',
        headers: cartHeaders(jwt),
      }),
    }),
    getCart: builder.query<SavedCart, Auth & { id: string }>({
      query: ({ jwt, id }) => ({
        url: API_ROUTES.SAVED_DONOR_SHOPPING_CART(id),
        headers: cartHeaders(jwt),
        timeout: RESTORE_REQUEST_TIMEOUT_MS,
      }),
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useCreateCartMutation,
  useUpdateCartMutation,
  useGetCurrentCartMutation,
  useCompleteCartMutation,
  useLazyGetCartQuery,
} = savedCartApi;
